(() => {
  "use strict";

  const SYSTEM_SELECTOR =
    "[data-a-sterneglanz-product-system]";

  const SOURCE_SELECTOR =
    "[data-a-sterneglanz-product-source]";

  const GENERATED_SELECTOR =
    "[data-a-sterneglanz-product-generated]";

  const NAVIGATION_SELECTOR =
    "[data-a-sterneglanz-product-navigation]";

  const CONTENT_SELECTOR =
    "[data-a-sterneglanz-product-content]";

  const TAB_SELECTOR =
    ".a-sterneglanz-product-system__tab";

  const PANEL_SELECTOR =
    ".a-sterneglanz-product-system__panel";

  /**
   * Erstellt eine sichere ID aus einem Text.
   */
  function createSafeId(value) {
    const safeId = String(value || "")
      .trim()
      .toLowerCase()
      .replace(/ä/g, "ae")
      .replace(/ö/g, "oe")
      .replace(/ü/g, "ue")
      .replace(/ß/g, "ss")
      .replace(/[^a-z0-9_-]+/g, "-")
      .replace(/^-+|-+$/g, "");

    return safeId || "produktinformation";
  }

  /**
   * Prüft, ob ein Element eine H2- oder H3-Überschrift ist.
   */
  function isSectionHeading(node) {
    return (
      node.nodeType === Node.ELEMENT_NODE &&
      ["H2", "H3"].includes(node.tagName)
    );
  }

  /**
   * Prüft, ob ein Knoten sichtbaren Inhalt besitzt.
   */
  function nodeHasContent(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent.trim() !== "";
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return false;
    }

    if (
      ["SCRIPT", "STYLE", "TEMPLATE", "NOSCRIPT"].includes(
        node.tagName
      )
    ) {
      return false;
    }

    return (
      node.textContent.trim() !== "" ||
      node.querySelector(
        "img, video, iframe, svg, picture"
      ) !== null
    );
  }

  /**
   * Prüft, ob eine erzeugte Sektion Inhalt besitzt.
   */
  function sectionHasContent(section) {
    return (
      section.title.trim() !== "" &&
      section.nodes.some(nodeHasContent)
    );
  }

  /**
   * Ermittelt den tatsächlichen Beschreibungscontainer.
   *
   * Shopify gibt den Inhalt in unserem Source-Element aus.
   * Innerhalb davon befindet sich bei deinen Produkten:
   *
   * <div class="product-description">...</div>
   */
  function getDescriptionContainer(source) {
    return (
      source.querySelector(".product-description") ||
      source
    );
  }

  /**
   * Zerlegt die Produktbeschreibung anhand aller H2- und H3-Elemente.
   *
   * Beispiel:
   * H2 Artikelbeschreibung
   * Absatz
   * H3 Produkttyp
   * Absatz
   * H3 Material
   * Absatz
   */
  function parseDescription(source) {
    const descriptionContainer =
      getDescriptionContainer(source);

    const originalNodes = Array.from(
      descriptionContainer.childNodes
    );

    const sections = [];
    let currentSection = null;

    originalNodes.forEach((node) => {
      if (isSectionHeading(node)) {
        const title = node.textContent.trim();

        if (!title) {
          return;
        }

        currentSection = {
          title,
          nodes: []
        };

        sections.push(currentSection);
        return;
      }

      if (!nodeHasContent(node)) {
        return;
      }

      /**
       * Falls vor der ersten H2/H3 bereits Text steht,
       * wird daraus automatisch eine Übersichtskarte.
       */
      if (!currentSection) {
        currentSection = {
          title: "Übersicht",
          nodes: []
        };

        sections.push(currentSection);
      }

      /**
       * Wichtig:
       * Wir verwenden cloneNode(true), damit die originale
       * Produktbeschreibung nicht zerstört oder verschoben wird.
       */
      currentSection.nodes.push(
        node.cloneNode(true)
      );
    });

    return sections.filter(sectionHasContent);
  }

  /**
   * Aktiviert die gewählte Karte.
   */
  function activateTab(
    system,
    selectedIndex,
    focusTab = false
  ) {
    const tabs = Array.from(
      system.querySelectorAll(TAB_SELECTOR)
    );

    const panels = Array.from(
      system.querySelectorAll(PANEL_SELECTOR)
    );

    tabs.forEach((tab, index) => {
      const isActive = index === selectedIndex;

      tab.setAttribute(
        "aria-selected",
        isActive ? "true" : "false"
      );

      tab.setAttribute(
        "tabindex",
        isActive ? "0" : "-1"
      );

      tab.classList.toggle(
        "is-active",
        isActive
      );

      if (isActive && focusTab) {
        tab.focus({
          preventScroll: true
        });
      }
    });

    panels.forEach((panel, index) => {
      const isActive = index === selectedIndex;

      panel.hidden = !isActive;

      panel.classList.toggle(
        "is-active",
        isActive
      );
    });
  }

  /**
   * Ermöglicht die Bedienung mit der Tastatur.
   */
  function handleTabKeyboard(
    event,
    system,
    currentIndex
  ) {
    const tabs = Array.from(
      system.querySelectorAll(TAB_SELECTOR)
    );

    if (!tabs.length) {
      return;
    }

    let targetIndex = currentIndex;

    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        targetIndex =
          (currentIndex + 1) % tabs.length;
        break;

      case "ArrowLeft":
      case "ArrowUp":
        targetIndex =
          (currentIndex - 1 + tabs.length) %
          tabs.length;
        break;

      case "Home":
        targetIndex = 0;
        break;

      case "End":
        targetIndex = tabs.length - 1;
        break;

      default:
        return;
    }

    event.preventDefault();

    activateTab(
      system,
      targetIndex,
      true
    );
  }

  /**
   * Erstellt einen Tab-Button.
   */
  function createTab({
    title,
    tabId,
    panelId,
    index,
    system
  }) {
    const tab =
      document.createElement("button");

    tab.type = "button";
    tab.id = tabId;
    tab.role = "tab";
    tab.textContent = title;

    tab.className =
      "a-sterneglanz-product-system__tab";

    if (index === 0) {
      tab.classList.add("is-active");
    }

    tab.setAttribute(
      "aria-controls",
      panelId
    );

    tab.setAttribute(
      "aria-selected",
      index === 0 ? "true" : "false"
    );

    tab.setAttribute(
      "tabindex",
      index === 0 ? "0" : "-1"
    );

    tab.addEventListener("click", () => {
      activateTab(system, index);
    });

    tab.addEventListener(
      "keydown",
      (event) => {
        handleTabKeyboard(
          event,
          system,
          index
        );
      }
    );

    return tab;
  }

  /**
   * Erstellt die zugehörige Inhaltskarte.
   */
  function createPanel({
    section,
    tabId,
    panelId,
    index
  }) {
    const panel =
      document.createElement("section");

    const panelInner =
      document.createElement("div");

    panel.id = panelId;
    panel.role = "tabpanel";
    panel.hidden = index !== 0;

    panel.className =
      "a-sterneglanz-product-system__panel";

    if (index === 0) {
      panel.classList.add("is-active");
    }

    panel.setAttribute(
      "aria-labelledby",
      tabId
    );

    panel.setAttribute(
      "tabindex",
      "0"
    );

    panelInner.className =
      "a-sterneglanz-product-system__panel-inner";

    section.nodes.forEach((node) => {
      panelInner.appendChild(node);
    });

    panel.appendChild(panelInner);

    return panel;
  }

  /**
   * Leert das erzeugte System vor einer Neuinitialisierung.
   */
  function resetSystem(system) {
    const source =
      system.querySelector(SOURCE_SELECTOR);

    const generated =
      system.querySelector(GENERATED_SELECTOR);

    const navigation =
      system.querySelector(NAVIGATION_SELECTOR);

    const content =
      system.querySelector(CONTENT_SELECTOR);

    if (navigation) {
      navigation.replaceChildren();
    }

    if (content) {
      content.replaceChildren();
    }

    if (generated) {
      generated.hidden = true;
    }

    if (source) {
      source.hidden = false;
    }

    system.classList.remove("is-ready");

    delete system.dataset.initialized;
  }

  /**
   * Baut das vollständige Kartensystem auf.
   */
  function initializeSystem(system) {
    if (
      system.dataset.initialized === "true"
    ) {
      return;
    }

    const source =
      system.querySelector(SOURCE_SELECTOR);

    const generated =
      system.querySelector(GENERATED_SELECTOR);

    const navigation =
      system.querySelector(NAVIGATION_SELECTOR);

    const content =
      system.querySelector(CONTENT_SELECTOR);

    if (
      !source ||
      !generated ||
      !navigation ||
      !content
    ) {
      return;
    }

    navigation.replaceChildren();
    content.replaceChildren();

    const sections =
      parseDescription(source);

    if (!sections.length) {
      source.hidden = false;
      generated.hidden = true;
      return;
    }

    const blockId = createSafeId(
      system.dataset.blockId || "product"
    );

    sections.forEach(
      (section, index) => {
        const titleId =
          createSafeId(section.title);

        const tabId =
          `a-sterneglanz-tab-${blockId}-${index}-${titleId}`;

        const panelId =
          `a-sterneglanz-panel-${blockId}-${index}-${titleId}`;

        const tab = createTab({
          title: section.title,
          tabId,
          panelId,
          index,
          system
        });

        const panel = createPanel({
          section,
          tabId,
          panelId,
          index
        });

        navigation.appendChild(tab);
        content.appendChild(panel);
      }
    );

    /**
     * Die originale Beschreibung wird erst ausgeblendet,
     * nachdem die Karten erfolgreich erzeugt wurden.
     */
    source.hidden = true;
    generated.hidden = false;

    system.classList.add("is-ready");
    system.dataset.initialized = "true";

    activateTab(system, 0);
  }

  /**
   * Initialisiert alle Sterneglanz-Blöcke
   * innerhalb des angegebenen Containers.
   */
  function initializeAllSystems(
    container = document
  ) {
    if (
      container instanceof Element &&
      container.matches(SYSTEM_SELECTOR)
    ) {
      initializeSystem(container);
    }

    container
      .querySelectorAll(SYSTEM_SELECTOR)
      .forEach((system) => {
        initializeSystem(system);
      });
  }

  /**
   * Initialisierung beim normalen Seitenaufruf.
   */
  function boot() {
    initializeAllSystems(document);
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      boot,
      {
        once: true
      }
    );
  } else {
    boot();
  }

  /**
   * Shopify Theme-Editor:
   * Section wurde neu geladen.
   */
  document.addEventListener(
    "shopify:section:load",
    (event) => {
      initializeAllSystems(event.target);
    }
  );

  /**
   * Shopify Theme-Editor:
   * Block wurde ausgewählt.
   */
  document.addEventListener(
    "shopify:block:select",
    (event) => {
      const system =
        event.target.closest(
          SYSTEM_SELECTOR
        );

      if (!system) {
        return;
      }

      if (
        system.dataset.initialized !== "true"
      ) {
        initializeSystem(system);
      }
    }
  );

  /**
   * Shopify Theme-Editor:
   * Block wurde geladen oder aktualisiert.
   */
  document.addEventListener(
    "shopify:block:load",
    (event) => {
      const system =
        event.target.closest(
          SYSTEM_SELECTOR
        );

      if (system) {
        resetSystem(system);
        initializeSystem(system);
        return;
      }

      initializeAllSystems(event.target);
    }
  );
})();